import { Check, DotsThree } from '@phosphor-icons/react';
import {
  cn,
  IconButton,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from '@reelplicate/ui';

import { CaptionPreview } from './caption-preview';
import { SOURCE_LABELS, type StyleOption } from './caption-style-options';

/**
 * A caption style to pick (Figma "Caption / Style card", 394:32219): its
 * preview on a sample slide, name and source. Selected shows the brand ring
 * and tick; custom styles can have an Edit / Duplicate / Delete menu.
 */
export function StyleCard({
  option,
  selected = false,
  note,
  imageUrl,
  onSelect,
  onEdit,
  onDuplicate,
  onDelete,
}: {
  option: StyleOption;
  selected?: boolean;
  /** Replaces the source line, e.g. "TikTok · Carousel style". */
  note?: string;
  imageUrl?: string | null;
  onSelect?: () => void;
  onEdit?: () => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
}) {
  const detail =
    note ??
    (option.source === 'custom'
      ? `${SOURCE_LABELS.custom} · ${option.usedBy === 1 ? '1 flow' : `${option.usedBy ?? 0} flows`}`
      : SOURCE_LABELS[option.source]);
  const hasMenu = Boolean(onEdit ?? onDuplicate ?? onDelete);
  return (
    <div
      className={cn(
        'bg-bg-surface relative flex w-38 flex-col gap-2 rounded-lg border p-1.5 pb-2',
        selected
          ? 'border-brand-primary ring-brand-primary ring-1'
          : 'border-border-subtle',
      )}
    >
      <button
        type="button"
        aria-pressed={onSelect ? selected : undefined}
        aria-label={`${option.name}, ${detail}`}
        onClick={onSelect ?? onEdit}
        className="focus-visible:shadow-focus-ring flex flex-col gap-2 rounded-md text-left outline-none"
      >
        <CaptionPreview
          style={option.style}
          imageUrl={imageUrl}
          width={140}
          label={`${option.name} preview`}
        />
        <span className="flex flex-col px-0.5">
          <span className="text-label-md text-text-primary truncate">{option.name}</span>
          <span className="text-caption text-text-secondary truncate">{detail}</span>
        </span>
      </button>
      {selected && (
        <span className="bg-brand-primary text-text-on-brand absolute top-3 right-3 flex size-5.5 items-center justify-center rounded-full">
          <Check weight="bold" className="size-3" aria-hidden />
        </span>
      )}
      {hasMenu && (
        <Menu>
          <MenuTrigger asChild>
            <IconButton
              type="button"
              icon={DotsThree}
              size="sm"
              variant="secondary"
              aria-label={`${option.name} actions`}
              className="absolute top-3 left-3 shadow-sm"
            />
          </MenuTrigger>
          <MenuContent align="start">
            {onEdit && <MenuItem onSelect={onEdit}>Edit</MenuItem>}
            {onDuplicate && <MenuItem onSelect={onDuplicate}>Duplicate</MenuItem>}
            {onDelete && (
              <MenuItem type="danger" onSelect={onDelete}>
                Delete
              </MenuItem>
            )}
          </MenuContent>
        </Menu>
      )}
    </div>
  );
}
